import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import fileDownload from 'js-file-download';
import {GridSortItem} from '@mui/x-data-grid-pro';

import {clientsUnitsStore} from '@store/clients/units/clients-units.store';
import {
  getClientsUnitsDataSource,
  putClientUnitFacturationModeDataSource
} from '@store/clients/units/clients-units.requests';
import {Pagination} from '@store/common/pagination.model';
import {ClientsUnitsFilters, ClientsUnitsPagination} from '@store/clients/units/clients-units.model';
import {ManageContact, ManageOrganisation, ManageOrganisationUnit} from '@store/common/organisation.model';
import {GlobalStatus} from '@store/common/globalStatus.enum';
import {formatFacturationModeToUpdateDto} from '@store/clients/details/facturationMode/facturation-mode.utils';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';

import {TableConfig} from '@constants/table.constants';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {TableFilter} from '@store/common/tableFilter.model';
import {Array} from '@utils/extensions/array.extension';
import {OrganisationStatus} from '@store/common/organisation.enums';

export class ClientsUnitsService {
  store = clientsUnitsStore;

  resetStore = () => this.store.reset();

  setStatus = (status?: GlobalStatus) => this.store.update((state) => ({...state, status}));
  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (clientId?: string) => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: ClientsUnitsFilters.initTableFilters(clientId)},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  searchClientsUnits = (search?: string, clientIds?: (string | number)[]): Observable<ClientsUnitsPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETClientsUnits(),
      params: {
        clientOrganisationIds: Array.formatJson(Array.mapNotNull(clientIds)),
        searchString: search || undefined,
        sortBy: 'organisationUnit.name',
        orderBy: 'ASC',
        page: 1,
        limit: TableConfig.pagination,
        status: OrganisationStatus.ACTIVE
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsUnitsPagination>) => {
          return response.data;
        }),
      );
  };

  getClientsUnits = (): Observable<ClientsUnitsPagination> => {
    const {status, filters: {search, tableFilters}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsUnits(),
      params: {
        ...requestFilters,
        status,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsUnitsPagination>) => {
          return response.data;
        }),
        tap((clientsUnitsPagination) => {
          const paginationData = Pagination.toPaginationData(clientsUnitsPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(clientsUnitsPagination.data),
            upsertEntitiesById(clientsUnitsPagination.data.map((client) => client.id), {
              updater: (clientsUnitUi) => clientsUnitUi,
              creator: (clientsUnitId) => ({id: clientsUnitId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, clientsUnitsPagination.data.map((clientsUnit) => clientsUnit.id)),
            getClientsUnitsDataSource.setSuccess(),
          );
        }),
        getClientsUnitsDataSource.trackRequestStatus(),
      );
  };

  getClientsUnitsCSV = (): Observable<Blob> => {
    const {status, filters: {search, tableFilters}, sort} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsUnitsCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        status,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => {
          return response.data;
        }),
        tap((data) => {
          const date = new Date();
          const formattedDate =
            date.getDate() + '_' + (date.getMonth() + 1) + '_' + date.getFullYear();
          fileDownload(data, `client_organisation${!requestFilters.clientOrganisationIds ? 's' : ''}_units_${formattedDate}.csv`);
        }),
      );
  };

  updateClientsUnitsStatusInList = (clientsUnitId: number | string, status: GlobalStatus): Observable<ClientsUnitsPagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHClientsUnitStatus(clientsUnitId),
      data: {status},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientsUnits()),
      );
  };

  deleteClientsUnitInList = (clientsUnitId: number | string): Observable<ClientsUnitsPagination> => {
    return from(APIAxios(APIRoutes.DELETEClientsUnit(clientsUnitId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientsUnits()),
      );
  };

  getClientUnitById = (clientUnitId: string | number): Observable<ManageOrganisationUnit> => {
    return from(APIAxios(APIRoutes.GETClientUnitById(clientUnitId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisationUnit>) => {
          return response.data;
        }),
      );
  };

  updateClientsUnitsStatus = (clientsUnitId: number | string, status: GlobalStatus): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.PATCHClientsUnitStatus(clientsUnitId),
      data: {status},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  createClientUnit = (clientId: string | number, data: ManageOrganisationUnit): Observable<ManageOrganisationUnit> => {
    return from(APIAxios({
      ...APIRoutes.POSTClientsUnit(clientId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisationUnit>) => {
          return response.data;
        }),
      );
  };

  updateClientUnitById = (clientUnitId: string | number, data: ManageOrganisationUnit): Observable<ManageOrganisationUnit> => {
    return from(APIAxios({
      ...APIRoutes.PUTClientUnitById(clientUnitId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisationUnit>) => {
          return response.data;
        }),
      );
  };

  deleteClientsUnit = (clientsUnitId: number | string): Observable<AxiosResponse> => {
    return from(APIAxios(APIRoutes.DELETEClientsUnit(clientsUnitId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  createClientsUnitContact = (clientsUnitId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.POSTClientsUnitContact(clientsUnitId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientUnitById(clientsUnitId)),
      );
  };

  updateClientsUnitContact = (clientsUnitId: number | string, contactId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTClientsUnitContactById(clientsUnitId, contactId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientUnitById(clientsUnitId)),
      );
  };

  deleteClientsUnitContact = (clientsUnitId: number | string, contactId: number | string): Observable<ManageOrganisation> => {
    return from(APIAxios(APIRoutes.DELETEClientsUnitContact(clientsUnitId, contactId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientUnitById(clientsUnitId)),
      );
  };

  setSelectedClientsUnits = (clientsUnitIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (clientsUnitsUI) => ({...clientsUnitsUI, selected: clientsUnitIds.includes(clientsUnitsUI.id)}),
        {ref: UIEntitiesRef},
      ),
    );
  };

  updateClientUnitFacturationMode = (clientUnitId: number | string, facturationModeId: number | string, existingData: FacturationMode, data: FacturationMode): Observable<FacturationMode> => {
    return from(APIAxios({
      ...APIRoutes.PUTClientUnitFacturationModeById(clientUnitId, facturationModeId),
      data: formatFacturationModeToUpdateDto(existingData, data),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<FacturationMode>) => response.data),
        tap(() =>
          this.store.update(
            putClientUnitFacturationModeDataSource.setSuccess(),
          )
        ),
        putClientUnitFacturationModeDataSource.trackRequestStatus(),
      );
  };
}

export const clientsUnitsService = new ClientsUnitsService();
